import type { MutableRefObject } from "react";
import { RichTextSubject } from "@/modules/froalaToolbar/shared/RichTextSubject";
import type {
  RichTextHighlight,
  RichTextRkpi,
  RichTextStyle,
} from "@library-elements/text";
import type { FroalaEditorInstance } from "./types";
import { DEFAULT_LINE_HEIGHT } from "../../constants";
import { getDeepestElementNode } from "../../utils";
import { getHighlightSpanInParents } from "../../utils/highlight";
import { getLinkValues } from "../../utils/links";
import { getListIcon, getListType } from "../../utils/list";
import { getRkpiDataFromAttrs } from "../../utils/rkpi";
import { getTextAnimationsInSelection } from "../../utils/textAnimation";
import { MAX_PARENT_WALK_DEPTH } from "./constants";
import { isFormatUniform } from "./format-utils";
import { getPending, hasPending } from "./pending-format-state";
import { getEditorOwnedRange, updateSelection } from "./selection-manager";

/** Walk parent chain from a node up to fr-view, returning tag names and detected text style. */
const walkParentChain = (
  startNode: Node,
): { nodesChain: string[]; textStyle: RichTextStyle | null } => {
  const nodesChain: string[] = [];
  let depth = MAX_PARENT_WALK_DEPTH;
  let current: Node | null = startNode;
  while (depth > 0 && current != null) {
    depth--;
    if (current instanceof HTMLElement && current.classList.contains("fr-view"))
      break;
    nodesChain.push(current.nodeName);
    current = current.parentElement;
  }
  let textStyle: RichTextStyle | null = null;
  if (nodesChain.includes("P")) textStyle = "normal";
  else if (nodesChain.includes("H1")) textStyle = "display";
  else if (nodesChain.includes("H2")) textStyle = "slide-title";
  else if (nodesChain.includes("H3")) textStyle = "heading";
  else if (nodesChain.includes("H4")) textStyle = "subheading";
  return { nodesChain, textStyle };
};

/**
 * Font size most of the text renders at. A collapsed caret makes the font-size
 * control apply to the whole text (see handleFontSize), and the caret is often
 * parked at the end of the last block, so reading the caret's size alone shows
 * a trailing line's size instead of what the text box mostly uses.
 */
export const getDominantFontSize = (root: HTMLElement): string | null => {
  const lengths = new Map<string, number>();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const length = n.textContent?.trim().length ?? 0;
    if (!length || !n.parentElement) continue;
    const size = window.getComputedStyle(n.parentElement).fontSize;
    if (size) lengths.set(size, (lengths.get(size) ?? 0) + length);
  }
  let best: string | null = null;
  lengths.forEach((length, size) => {
    if (best == null || length > (lengths.get(best) ?? 0)) best = size;
  });
  return best;
};

export const createRefresh = (
  id: string,
  froalaInstance: MutableRefObject<FroalaEditorInstance | null>,
  lastRange: MutableRefObject<Range | null>,
  selectedText: MutableRefObject<string | null>,
  setAmountOfLines: (n: number) => void,
) => {
  const refresh = (clearSelection = false, shouldSaveLastRange = true) => {
    const editor = froalaInstance.current;
    if (!editor) return;

    if (clearSelection) {
      const deepestNode = getDeepestElementNode(editor.$el.get(0));
      editor.selection.setAtEnd(deepestNode);
      editor.selection.restore();
      editor.$el.blur();
    }

    if (shouldSaveLastRange)
      updateSelection(froalaInstance, lastRange, selectedText);

    const el = (editor.selection?.element() ?? editor.$el?.[0]) as HTMLElement;
    if (!el) return;

    if (editor.$el[0]) {
      const lines = editor.$el[0].querySelectorAll("p, h2, h3, li");
      setAmountOfLines(lines.length);
    }

    const currentBlock = editor.selection?.blocks()[0] ?? el;
    const computedStyle = window.getComputedStyle(el);
    const blockComputedStyle = window.getComputedStyle(currentBlock);
    const dsComputedStyle = window.getComputedStyle(document.documentElement);

    const { color, textAlign, direction } = computedStyle;
    let { fontSize } = computedStyle;
    let lineHeight = blockComputedStyle.lineHeight;

    let textColor = color;
    const highlightSpan = getHighlightSpanInParents(el);
    if (highlightSpan) {
      const paragraph = editor.node.blockParent(el);
      if (paragraph) textColor = editor.$(paragraph).css("color");
    }

    const aiClassification = el.getAttribute("data-ai-classification")
      ? el
      : el.closest("[data-ai-classification]");

    const sel = window.getSelection();
    const root = editor.$el?.[0];
    const range = root
      ? getEditorOwnedRange(root, sel, lastRange.current)
      : sel && sel.rangeCount > 0
        ? sel.getRangeAt(0)
        : null;
    let textStyle: RichTextStyle | null = null;
    let selectedRkpi: RichTextRkpi | null = null;
    let isBold = false;
    let isItalic = false;
    let isStrikethrough = false;
    let isUnderline = false;
    let isSubscript = false;
    let isSuperscript = false;

    if (range) {
      const isCollapsed = range.collapsed;
      if (isCollapsed && root) fontSize = getDominantFontSize(root) ?? fontSize;

      if (isCollapsed) {
        // Collapsed cursor: walk parent chain (original behavior)
        const { nodesChain, textStyle: chainTextStyle } = walkParentChain(
          range.endContainer,
        );

        isBold = nodesChain.includes("STRONG");
        isItalic = nodesChain.includes("EM");
        isUnderline = nodesChain.includes("U");
        isStrikethrough = nodesChain.includes("S");
        isSubscript = nodesChain.includes("SUB");
        isSuperscript = nodesChain.includes("SUP");

        textStyle = chainTextStyle;
      } else if (root) {
        // Selection: use isFormatUniform for accurate multi-node detection (Phase 7)
        isBold = isFormatUniform("bold", root);
        isItalic = isFormatUniform("italic", root);
        isUnderline = isFormatUniform("underline", root);
        isStrikethrough = isFormatUniform("strikethrough", root);
        isSubscript = isFormatUniform("subscript", root);
        isSuperscript = isFormatUniform("superscript", root);

        // Text style from end container (keep original)
        textStyle = walkParentChain(range.endContainer).textStyle;
      }

      if (range.commonAncestorContainer instanceof HTMLElement && sel) {
        const rkpis =
          range.commonAncestorContainer.querySelectorAll("[data-rkpi-active]");
        for (let i = 0; i < rkpis.length; i++) {
          const rkpi = rkpis[i];
          if (sel.containsNode(rkpi, true)) {
            selectedRkpi = getRkpiDataFromAttrs(rkpi);
            break;
          }
        }
      }

      if (lineHeight === "normal" || lineHeight == null || fontSize == null) {
        lineHeight = DEFAULT_LINE_HEIGHT;
      } else {
        const parsedLineHeight = Number.parseFloat(
          lineHeight.replace("px", ""),
        );
        const parsedFontSize = Number.parseFloat(
          blockComputedStyle.fontSize.replace("px", ""),
        );

        if (Number.isNaN(parsedLineHeight) || Number.isNaN(parsedFontSize))
          lineHeight = DEFAULT_LINE_HEIGHT;
        else lineHeight = `${parsedLineHeight / parsedFontSize}`;
      }

      if (!textStyle) {
        const fontSize = Number.parseFloat(
          computedStyle.fontSize.replace("px", ""),
        );
        const sizeFor = (prop: string) =>
          Number.parseFloat(
            dsComputedStyle.getPropertyValue(prop).replace("px", ""),
          );
        const sizeMatches: Array<[number, RichTextStyle]> = [
          [sizeFor("--display-size"), "display"],
          [sizeFor("--title-size"), "slide-title"],
          [sizeFor("--heading-size"), "heading"],
          [sizeFor("--subtitle-size"), "subheading"],
        ];

        textStyle =
          sizeMatches.find(([size]) => size === fontSize)?.[1] ?? "normal";
      }
    }

    // Overlay pending format state (Phase 1)
    if (hasPending()) {
      const pending = getPending();
      if (pending.bold !== undefined) isBold = pending.bold;
      if (pending.italic !== undefined) isItalic = pending.italic;
      if (pending.underline !== undefined) isUnderline = pending.underline;
      if (pending.strikethrough !== undefined)
        isStrikethrough = pending.strikethrough;
      if (pending.subscript !== undefined) isSubscript = pending.subscript;
      if (pending.superscript !== undefined)
        isSuperscript = pending.superscript;
    }

    const textAnimations = getTextAnimationsInSelection(editor);

    RichTextSubject.emit({
      type: "refresh",
      editorId: id,
      data: {
        selectedRkpi,
        fontSize: Number.parseFloat(fontSize),
        textAlign,
        textColor: textColor,
        isBold,
        isItalic,
        isUnderline,
        textStyle,
        isStrikethrough,
        textDirection: direction as "ltr" | "rtl",
        highlight: highlightSpan
          ? {
              bgColor: highlightSpan.dataset.highlightBgColor ?? "",
              textColor: highlightSpan.dataset.highlightTextColor ?? "",
              animation: highlightSpan.dataset
                .highlightAnimation as RichTextHighlight["animation"],
              static: highlightSpan.dataset.highlightStatic === "true",
            }
          : null,
        listType: getListType(el),
        listIcon: getListIcon(el),
        lineHeight,
        link: getLinkValues(el),
        subscript: isSubscript,
        superscript: isSuperscript,
        selectionContent: range?.toString(),
        textAnimation: textAnimations.length > 0 ? textAnimations[0] : null,
        aiClassification: aiClassification?.getAttribute(
          "data-ai-classification",
        ),
      },
    });
  };

  return refresh;
};
